<template>
  <div
    v-if="open"
    class="fixed inset-0 flex items-start justify-center overflow-y-auto p-3 sm:p-6"
    style="z-index: var(--z-modal, 1000); background: rgba(0, 0, 0, 0.45);">
    <div
      ref="dialogEl"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="headingId"
      class="landing-publish-dialog w-full max-w-2xl rounded-lg p-4 sm:p-6 space-y-5 shadow-xl"
      style="background: var(--bg-surface-elevated, #fff); color: var(--text-primary); border: 1px solid var(--border-primary, #e5e7eb);"
      @keydown.esc.prevent="emit('close')"
      @keydown.tab="trapFocus">
      <div class="flex items-start justify-between gap-3">
        <h2 :id="headingId" ref="headingEl" tabindex="-1" class="text-lg font-semibold break-words">
          Publish “{{ page.title }}”
        </h2>
        <button type="button" class="btn btn-secondary" data-action="close" @click="emit('close')">
          Close
        </button>
      </div>

      <section aria-labelledby="publish-version-heading" class="space-y-2">
        <h3 id="publish-version-heading" class="font-medium">
          Version to publish
        </h3>
        <p v-if="revisionState === 'loading'" class="text-sm" style="color: var(--text-secondary);">
          Loading saved versions…
        </p>
        <p v-else-if="revisionState === 'error'" role="alert" class="text-sm text-red-700">
          Couldn't load saved versions.
          <button type="button" class="underline" @click="loadRevisions()">
            Try again
          </button>
        </p>
        <div v-else-if="!admissible.length" class="text-sm" style="color: var(--text-secondary);" data-test="no-versions">
          This page has no saved version yet. Preview the page or save the current draft as a version first.
        </div>
        <label v-else class="flex flex-col text-sm">
          <span class="sr-only">Version to publish</span>
          <select v-model="selectedId" data-test="checkpoint" class="form-select">
            <option v-for="revision in admissible" :key="revision.id" :value="revision.id">
              Saved {{ formatTime(revision.created_at) }}{{ revision.published ? ' (live now)' : '' }}
            </option>
          </select>
        </label>
        <button
          type="button"
          class="btn btn-secondary"
          data-action="save-version"
          :disabled="busy.checkpoint"
          @click="saveVersion()">
          {{ busy.checkpoint ? 'Saving…' : 'Save current draft as a version' }}
        </button>
      </section>

      <section v-if="selected" aria-labelledby="publish-url-heading" class="space-y-1 text-sm">
        <h3 id="publish-url-heading" class="font-medium">
          Address
        </h3>
        <template v-if="urlChanged">
          <p>Currently live at <span class="break-all font-mono" data-test="old-url">{{ oldUrl }}</span></p>
          <p>Will publish to <span class="break-all font-mono" data-test="new-url">{{ newUrl }}</span></p>
          <p style="color: var(--text-secondary);">
            Links to the old address keep working as a redirect.
          </p>
        </template>
        <p v-else>
          <span class="break-all font-mono" data-test="new-url">{{ newUrl }}</span>
        </p>
      </section>

      <section aria-labelledby="publish-impact-heading" class="space-y-2 text-sm">
        <h3 id="publish-impact-heading" class="font-medium">
          What this affects
        </h3>
        <p v-if="impactState === 'loading'" style="color: var(--text-secondary);">
          Checking where this page is used…
        </p>
        <div v-else-if="impactState === 'unavailable'" data-test="impact-unavailable" class="space-y-1">
          <p>{{ impactMessage }}</p>
          <button type="button" class="btn btn-secondary" data-action="check-usage" @click="checkUsage()">
            Check again
          </button>
        </div>
        <template v-else-if="impactState === 'ready'">
          <p v-if="!assignments.length" data-test="impact-none">
            No ads or other pages depend on this page.
          </p>
          <p v-else data-test="impact-assignments">
            {{ assignments.length }} ad placement(s) use this page. Publishing updates what they show.
          </p>
          <div v-if="publishedRoutes.length">
            <p style="color: var(--text-secondary);">
              Live now:
            </p>
            <ul class="list-disc pl-5">
              <li v-for="route in publishedRoutes" :key="route.path" class="break-all font-mono">
                {{ route.path }}
              </li>
            </ul>
          </div>
          <p v-else style="color: var(--text-secondary);">
            This page is not live yet.
          </p>
        </template>
      </section>

      <section v-if="selected" aria-labelledby="publish-ready-heading" class="space-y-2 text-sm">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h3 id="publish-ready-heading" class="font-medium">
            Before publishing
          </h3>
          <button
            type="button"
            class="btn btn-secondary"
            data-action="refresh-readiness"
            :disabled="busy.readiness"
            @click="refreshReadiness()">
            {{ busy.readiness ? 'Checking…' : 'Check again' }}
          </button>
        </div>
        <p v-if="readinessState === 'loading'" style="color: var(--text-secondary);">
          Checking what publishing needs…
        </p>
        <p v-else-if="readinessState === 'error'" role="alert" class="text-red-700">
          Couldn't check readiness. Try again in a moment.
        </p>
        <ul v-else-if="readiness" class="space-y-2 list-none p-0 m-0" data-test="readiness">
          <li
            v-for="check in readiness.checks"
            :key="check.key"
            class="rounded border p-2"
            style="border-color: var(--border-primary, #e5e7eb);"
            :data-test="`check-${check.key}`">
            <span class="font-medium">{{ CHECK_LABELS[check.key] || humanize(check.key) }}:&nbsp;</span>
            <span :class="stateClass(check.state)"> {{ STATE_LABELS[check.state] || 'Needs attention' }}</span>
            <span class="block" style="color: var(--text-secondary);">{{ check.message }}</span>
            <a
              v-if="check.action === 'call_settings' && callSettingsUrl && check.state !== 'ready'"
              :href="callSettingsUrl"
              class="underline">Open call and lead settings</a>
            <button
              v-else-if="check.action === 'refresh' && check.state !== 'ready'"
              type="button"
              class="underline"
              @click="recheckUsage()">
              Check usage again
            </button>
            <span v-else-if="check.action === 'wait' && check.state !== 'ready'" class="block" style="color: var(--text-secondary);">
              Nothing to do yet; check back shortly.
            </span>
          </li>
        </ul>
      </section>

      <p role="status" aria-live="polite" class="text-sm" data-test="operation-status">
        {{ operationLabel }}
      </p>
      <div v-if="stalled" class="text-sm space-y-2" data-test="operation-stalled" style="color: var(--text-secondary);">
        <p>
          Publishing is still {{ operation && operation.state === 'running' ? 'in progress' : 'queued' }}. It continues on our side;
          you can close this window and check again later.
        </p>
        <button type="button" class="btn btn-secondary btn-sm" data-action="check-status" @click="follow(operation.id)">
          Check status
        </button>
      </div>
      <p v-if="message" role="alert" class="text-sm" :class="message.kind === 'conflict' ? 'text-amber-800' : 'text-red-700'" data-test="message">
        {{ message.text }}
      </p>

      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" class="btn btn-secondary" @click="emit('close')">
          {{ operation && operation.state === 'succeeded' ? 'Done' : 'Cancel' }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          data-action="publish"
          :disabled="!canPublish"
          @click="publish()">
          {{ publishLabel }}
        </button>
      </div>
      <p v-if="!page.site || !page.site.manifest_version" class="text-sm" style="color: var(--text-secondary);">
        Publishing opens once Made.ly finishes setting up hosting for this site.
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { waitForOperation } from '~/javascript/services/landing_pages/api'
import { humanize } from './native_api'

const props = defineProps({
  api: { type: Object, required: true },
  page: { type: Object, required: true },
  open: { type: Boolean, default: true },
  callSettingsUrl: { type: String, default: '' },
  pollIntervalMs: { type: Number, default: 1500 },
  pollAttempts: { type: Number, default: 120 }
})
const emit = defineEmits(['close', 'published', 'error'])

const CHECK_LABELS = {
  hosting: 'Hosting',
  registry: 'Page usage',
  form_acceptance: 'Form submissions',
  call_routing: 'Call forwarding',
  event_delivery: 'Lead delivery',
  sample_text: 'Sample text'
}
const STATE_LABELS = {
  ready: 'Ready', not_applicable: 'Not needed', pending: 'Waiting', unknown: 'Needs attention', failed: 'Needs attention', warning: 'Review'
}
const OPERATION_LABELS = { queued: 'Queued', running: 'Publishing', succeeded: 'Published', failed: 'Publishing failed' }
const ERROR_MESSAGES = {
  version_conflict: 'This page changed; reload the page before publishing.',
  impact_unavailable: 'Page usage needs a fresh check before publishing.',
  tracking_not_ready: 'Something publishing needs is not ready yet. Review the list above.',
  domain_not_ready: 'The site address is not ready yet. Review the list above.',
  outcome_unknown: "We couldn't confirm the request; check the status before retrying.",
  resource_not_found: 'This page is no longer available to you.',
  field_invalid: 'The selected version cannot be published.'
}

const headingId = `publish-dialog-${Math.random().toString(36).slice(2)}`
const headingEl = ref(null)
const dialogEl = ref(null)
const revisionRows = ref([])
const revisionState = ref('loading')
const selectedId = ref('')
const impactState = ref('loading')
const impactData = ref(null)
const impactMessage = ref('')
const readiness = ref(null)
const readinessState = ref('idle')
const operation = ref(null)
const message = ref(null)
const busy = reactive({ checkpoint: false, readiness: false, publish: false })

const admissible = computed(() => revisionRows.value.filter((revision) => revision.admissible))
const selected = computed(() => admissible.value.find((revision) => revision.id === selectedId.value) || null)
const digest = computed(() => impactData.value?.impact?.digest || null)
const assignments = computed(() => impactData.value?.usage?.assignments || [])
const publishedRoutes = computed(() => impactData.value?.usage?.registry?.published_routes || [])
const newUrl = computed(() => {
  if (selected.value?.publication_route) return selected.value.publication_route
  const host = props.page.site?.default_hostname
  return host ? `https://${host}${props.page.path}` : props.page.path
})
const oldUrl = computed(() => props.page.canonical_url || null)
const urlChanged = computed(() => props.page.status === 'published' && oldUrl.value && oldUrl.value !== newUrl.value)
const inFlight = computed(() => busy.publish || (operation.value && ['queued', 'running'].includes(operation.value.state)))
const canPublish = computed(() => Boolean(selected.value && readiness.value?.ready && digest.value &&
  props.page.site?.manifest_version && !inFlight.value && operation.value?.state !== 'succeeded'))
const operationLabel = computed(() => {
  if (!operation.value) return busy.publish ? 'Sending…' : ''
  const label = OPERATION_LABELS[operation.value.state] || 'Status unknown'
  if (operation.value.state === 'queued') return 'Publishing… queued'
  if (operation.value.state === 'failed') {
    const detail = operation.value.error?.message
    return detail ? `${label}: ${detail}` : label
  }
  return label
})
const publishLabel = computed(() => {
  if (operation.value?.state === 'failed') return 'Try publishing again'
  return inFlight.value ? 'Publishing…' : 'Publish'
})

function formatTime(value) {
  return value ? new Date(value).toLocaleString() : ''
}

function stateClass(state) {
  if (state === 'ready') return 'text-green-700'
  if (state === 'not_applicable' || state === 'pending') return ''
  return 'text-amber-800'
}

function describe(error) {
  if (!error) return 'Something went wrong.'
  const code = error.status === 0 ? 'outcome_unknown' : error.code
  return ERROR_MESSAGES[code] || 'Something went wrong. Your draft is unchanged; try again.'
}

async function loadRevisions(preferId = null) {
  revisionState.value = 'loading'
  try {
    const result = await props.api.revisions(props.page.id)
    revisionRows.value = [...(result.data || [])]
      .sort((left, right) => new Date(right.created_at) - new Date(left.created_at))
    revisionState.value = 'ready'
    const keep = admissible.value.find((revision) => revision.id === (preferId || selectedId.value))
    selectedId.value = keep ? keep.id : (admissible.value[0]?.id || '')
  } catch (error) {
    revisionState.value = 'error'
  }
}

async function checkUsage() {
  impactState.value = 'loading'
  try {
    await props.api.observeRegistry(props.page.site.id)
    const result = await props.api.impact(props.page.id)
    impactData.value = result.data
    if (result.data?.impact?.digest) {
      impactState.value = 'ready'
    } else {
      impactState.value = 'unavailable'
      impactMessage.value = 'Page usage could not be confirmed right now. Publishing waits until it can be checked.'
    }
  } catch (error) {
    impactState.value = 'unavailable'
    impactMessage.value = error?.reason === 'native_registry_unavailable' || error?.code === 'impact_unavailable' ?
      'Page usage can be checked once hosting for this site is ready. Try again shortly.' :
      describe(error)
  }
}

async function loadReadiness(revisionId) {
  if (!revisionId) {
    readiness.value = null
    return
  }
  readinessState.value = 'loading'
  try {
    const result = await props.api.readiness(props.page.id, revisionId)
    if (revisionId === selectedId.value) {
      readiness.value = result.data
      readinessState.value = 'ready'
    }
  } catch (error) {
    readinessState.value = 'error'
  }
}

async function refreshReadiness() {
  if (!selectedId.value) return
  busy.readiness = true
  try {
    const result = await props.api.refreshReadiness(props.page.id, selectedId.value)
    readiness.value = result.data
    readinessState.value = 'ready'
  } catch (error) {
    readinessState.value = readiness.value ? 'ready' : 'error'
    message.value = { kind: 'error', text: describe(error) }
  } finally {
    busy.readiness = false
  }
}

async function recheckUsage() {
  await checkUsage()
  await loadReadiness(selectedId.value)
}

async function saveVersion() {
  busy.checkpoint = true
  message.value = null
  try {
    const result = await props.api.checkpoint(props.page.id, props.page.version)
    await loadRevisions(result.data?.id)
  } catch (error) {
    message.value = { kind: error?.code === 'version_conflict' ? 'conflict' : 'error', text: describe(error) }
  } finally {
    busy.checkpoint = false
  }
}

const stalled = ref(false)

// Polling is bounded; an unfinished operation stays recoverable with an
// explicit status check instead of a dead "queued" label.
async function follow(operationId) {
  stalled.value = false
  try {
    const final = await waitForOperation(props.api, operationId, {
      intervalMs: props.pollIntervalMs,
      attempts: props.pollAttempts,
      onUpdate: (current) => { operation.value = current }
    })
    if (final) operation.value = final
    if (final && !['succeeded', 'failed'].includes(final.state)) stalled.value = true
    if (final?.state === 'succeeded') emit('published', final)
  } catch (error) {
    message.value = { kind: 'error', text: "We couldn't read the publishing status. It may still be in progress; check again shortly." }
  }
}

async function publish() {
  if (!canPublish.value) return
  busy.publish = true
  message.value = null
  operation.value = null
  let queued = null
  try {
    const result = await props.api.publish(props.page.id, {
      revision_id: selectedId.value,
      expected_manifest_version: props.page.site.manifest_version,
      impact_digest: digest.value
    }, props.page.version)
    queued = result.data
    // A 202 only means the request was accepted; the operation state is the truth.
    operation.value = { ...queued, state: queued?.state === 'succeeded' ? 'queued' : (queued?.state || 'queued') }
  } catch (error) {
    const code = error?.status === 0 ? 'outcome_unknown' : error?.code
    message.value = { kind: code === 'version_conflict' ? 'conflict' : 'error', text: describe(error) }
    emit('error', error)
    if (code === 'impact_unavailable') await checkUsage()
    if (code === 'tracking_not_ready' || code === 'domain_not_ready' || code === 'outcome_unknown') {
      await loadReadiness(selectedId.value)
    }
  } finally {
    busy.publish = false
  }
  if (queued?.id) await follow(queued.id)
}

watch(selectedId, (value) => { loadReadiness(value) })

async function load() {
  await loadRevisions()
  await checkUsage()
}

// Keyboard focus stays inside the modal and returns to the opener on close.
let opener = null

function trapFocus(event) {
  const nodes = [...(dialogEl.value?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]') || [])]
  if (!nodes.length) return
  const first = nodes[0]
  const last = nodes[nodes.length - 1]
  const active = globalThis.document.activeElement
  if (event.shiftKey && (active === first || !dialogEl.value.contains(active))) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && (active === last || !dialogEl.value.contains(active))) {
    event.preventDefault()
    first.focus()
  }
}

onBeforeUnmount(() => {
  if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus()
})

onMounted(async() => {
  opener = globalThis.document.activeElement
  if (!props.open) return
  await nextTick()
  headingEl.value?.focus()
  await load()
})

watch(() => props.open, async(value) => {
  if (!value) return
  await nextTick()
  headingEl.value?.focus()
  await load()
})
</script>
